import React, { useState, useEffect, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import { Field, Input, Textarea, FormGrid } from '../../ui/Field/Field';
import { VehicleUploadCard, VehicleSearchCard, SelectedVehiclesCard } from './VehicleSelection';
import { fleetService, maintenanceService } from '../../services/firestore';
import { onRtaPages } from '../../services/fleetStatus';
import LoadingSpinner from '../../components/LoadingSpinner';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { useModal } from '../../contexts/ModalContext';
import { restoreSavedForm } from './govForms.logic';
import logger from '../../utils/logger';

/*
 * The one form behind the government letter pages (Vehicle Export, Hiaza, Ownership Transfer, Free Document,
 * Registration). Each page file only says what is its own: the browser key, the defaults, the fields, the letter
 * text, the file name. They were five copies of this code (review 18 Oct 2026); what each page shows, keeps and
 * prints is locked by govLetters.test.js.
 *
 * Vehicles come from the fleet (search + Excel of plates / VINs) or, with `upload`, only from an Excel of new
 * cars (Registration).
 *
 * On the Government Transactions page each one is a tab: `fleet` (govFleet.js) is the fleet read once for all tabs,
 * and the form comes without its own page frame. Without `fleet` it is a page of its own that reads the fleet itself.
 */

const BOLD = { fontSize: '12pt', fontWeight: 'bold' };
const cell = (width, textAlign) => ({ border: '1px solid black', padding: '3mm 6mm', width, fontSize: '12pt', fontWeight: 'bold', textAlign });

/** The vehicle rows of a fleet car in the letter table. */
export const fleetCarRows = (car) => [
    { label: 'صنف المركبة', value: `${car.make} ${car.model}` },
    { label: 'الموديـــــل', value: car.year },
    { label: 'رقم الشاصي', value: car.vin },
    { label: 'رقم اللوحة', value: car.plate_number },
    { label: 'اللـــــــــون', value: car.color }
];

const fleetConfirm = {
    title: 'Remove All Vehicles',
    message: 'Are you sure you want to remove all selected vehicles from this list?',
    startLabel: 'Remove All',
};

function FormField({ field, formData, setFormData }) {
    const value = formData[field.key];
    const onChange = (e) => setFormData({ ...formData, [field.key]: e.target.value });
    const rtl = field.rtl !== false;
    return (
        <Field label={field.label} help={field.help} className={field.full ? 'col-span-full' : undefined}>
            {field.rows ? (
                <Textarea
                    rows={field.rows}
                    className="text-right"
                    dir="rtl"
                    placeholder={field.placeholder}
                    value={value}
                    onChange={onChange}
                />
            ) : (
                <Input
                    className={rtl ? 'text-right' : undefined}
                    dir={rtl ? 'rtl' : undefined}
                    value={value}
                    onChange={onChange}
                />
            )}
        </Field>
    );
}

/** One A4 letter for one vehicle (captured into the PDF). */
function LetterSheet({ id, letter, formData, car }) {
    return (
        <div
            id={id}
            style={{
                width: '210mm',
                minHeight: '297mm',
                padding: '15mm 20mm 20mm 20mm',
                fontFamily: 'Arial, sans-serif',
                background: 'white',
                color: 'black',
                boxSizing: 'border-box',
                position: 'relative'
            }}
        >
            {/* Title */}
            <div style={{ textAlign: 'center', marginBottom: '15mm' }}>
                <h1 style={{ fontSize: '16pt', fontWeight: 'bold', borderBottom: '2px solid black', display: 'inline-block', paddingBottom: '2mm' }}>
                    {letter.heading ? letter.heading(formData) : formData.title}
                </h1>
            </div>

            {/* Top Info */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10mm', fontWeight: 'bold', fontSize: '12pt' }}>
                <div dir="rtl">التاريخ: {formData.date}</div>
            </div>

            {/* addressed to */}
            {letter.noCity ? (
                <div dir="rtl" style={{ ...BOLD, marginBottom: '8mm' }}>
                    {formData.toAddress}
                </div>
            ) : (
                <>
                    <div dir="rtl" style={{ ...BOLD, marginBottom: '4mm', whiteSpace: 'pre-wrap' }}>
                        {formData.toAddress}
                    </div>
                    <div dir="rtl" style={{ ...BOLD, marginBottom: '12mm' }}>
                        {formData.toCity}
                    </div>

                    <div dir="rtl" style={BOLD}>
                        تحية طيبة وبعد،،
                    </div>
                </>
            )}

            <div dir="rtl" style={{ ...BOLD, marginBottom: '8mm', lineHeight: '1.8', textAlign: 'justify', whiteSpace: 'pre-wrap' }}>
                {letter.intro(formData)}
            </div>

            {/* Vehicle Table */}
            <table dir="rtl" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '5mm', marginBottom: '10mm' }}>
                <tbody>
                    {letter.rows(car).map((row, rIdx) => (
                        <tr key={rIdx}>
                            <td style={cell('40%', 'right')}>{row.label}</td>
                            <td style={cell('60%', 'center')}>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {letter.afterTable && letter.afterTable(formData)}

            {/* Footer / Signature */}
            <div dir="rtl" style={{ marginTop: letter.footerGap || '10mm', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ...BOLD }}>
                <div style={{ marginBottom: '5mm' }}>وتفضلوا بقبول وافر الاحترام والتقدير.</div>
                <div>الوفاق السعودية لتأجير السيارات ذ.م.م.</div>
            </div>
        </div>
    );
}

const readSavedCars = (storageKey) => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
        try {
            return JSON.parse(saved).selectedCars || [];
        } catch (e) { return []; }
    }
    return [];
};

/**
 * page: { storageKey, title, detailsTitle, form (defaults after the date), fields, pdfName, pageId, containerId,
 *         containerColor, letter: { heading, intro, rows, noCity, afterTable, footerGap }, numbered, showYear,
 *         upload (Registration: { description, accept, template, templateName, parse, confirm, selectedCard }) }
 * fleet: { loading, letterCars } when the form is a tab of the Government Transactions page
 */
export default function GovLetterPage({ page, fleet, onBusyChange }) {
    const { showConfirm, showToast } = useModal();
    const fromFleet = !page.upload;
    // State
    const [ownCars, setAllCars] = useState([]);
    const [selectedCars, setSelectedCars] = useState(() => readSavedCars(page.storageKey));
    const [ownLoading, setLoading] = useState(fromFleet && !fleet);
    const allCars = fleet ? fleet.letterCars : ownCars;
    const loading = fleet ? fromFleet && fleet.loading : ownLoading;
    const [generating, setGenerating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // the saved form comes back, but the date always starts as today (restoreSavedForm)
    const [formData, setFormData] = useState(() => restoreSavedForm(
        localStorage.getItem(page.storageKey),
        { date: new Date().toLocaleDateString('en-GB'), ...page.form }
    ));

    const exportTemplateRef = useRef(null);
    const fileInputRef = useRef(null);

    // Initial Load
    useEffect(() => {
        if (fromFleet && !fleet) loadFleet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // the tabs page locks the other tabs while the PDF is built
    useEffect(() => {
        if (onBusyChange) onBusyChange(generating);
    }, [generating, onBusyChange]);

    // Persist to localStorage
    useEffect(() => {
        const dataToSave = { formData, selectedCars };
        localStorage.setItem(page.storageKey, JSON.stringify(dataToSave));
    }, [page.storageKey, formData, selectedCars]);

    const loadFleet = async () => {
        setLoading(true);
        try {
            const [data, statusResult] = await Promise.all([
                fleetService.getAllCars(false),
                maintenanceService.getStatusMap()
            ]);

            const statusMap = statusResult.map || statusResult || {};

            if (data) {
                const formatted = data
                    .map(car => ({
                        ...car,
                        current_km: statusMap[car.plate_number]?.km || car.current_km || 0
                    }))
                    .filter(onRtaPages);   // an RTA page: Ready To Sell stays (still registered); a car RTA dropped goes
                setAllCars(formatted);
            }
        } catch (error) {
            logger.error("Failed to load fleet", error);
        } finally {
            setLoading(false);
        }
    };

    // Filtered cars for selection
    const filteredCars = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return allCars.filter(c =>
            !selectedCars.find(sc => sc.plate_number === c.plate_number) &&
            (String(c.plate_number).toLowerCase().includes(q) ||
             String(c.make).toLowerCase().includes(q) ||
             String(c.model).toLowerCase().includes(q) ||
             String(c.vin).toLowerCase().includes(q))
        ).slice(0, 10);
    }, [allCars, searchQuery, selectedCars]);

    const handleSelectCar = (car) => {
        setSelectedCars([...selectedCars, car]);
        setSearchQuery('');
    };

    // fleet cars go by plate, uploaded new cars by their row id
    const handleRemoveCar = (key) => {
        setSelectedCars(selectedCars.filter(c => (fromFleet ? c.plate_number : c.id) !== key));
    };

    const handleClearAll = () => {
        showConfirm({
            ...(fromFleet ? fleetConfirm : page.upload.confirm),
            isDangerous: true,
            onConfirm: () => setSelectedCars([])
        });
    };

    // the fleet pages: plates or VINs in any cell, added to the list
    const handleFleetFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                const platesFromExcel = [];
                jsonData.forEach(row => {
                    row.forEach(cell => {
                        if (cell) {
                             const strVal = String(cell).trim().toLowerCase();
                             if (strVal) platesFromExcel.push(strVal);
                        }
                    });
                });

                const newSelected = [...selectedCars];
                let addedCount = 0;

                platesFromExcel.forEach(plate => {
                    const matchedCar = allCars.find(c =>
                        String(c.plate_number).toLowerCase() === plate ||
                        String(c.vin).toLowerCase() === plate
                    );

                    if (matchedCar && !newSelected.find(sc => sc.plate_number === matchedCar.plate_number)) {
                        newSelected.push(matchedCar);
                        addedCount++;
                    }
                });

                setSelectedCars(newSelected);
                showToast(`Successfully imported ${addedCount} vehicles.`, 'success');

            } catch (error) {
                logger.error("Error reading Excel", error);
                showToast("Failed to read Excel file. Please check the format.", 'error');
            }
        };

        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    // Registration: the rows of the Excel are the new cars (the list is replaced)
    const handleNewCarsUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws);

            const formattedCars = data.map(page.upload.parse);

            setSelectedCars(formattedCars);
            showToast(`Successfully imported ${formattedCars.length} vehicles.`, 'success');
        };
        reader.onerror = () => {
            showToast("Failed to read Excel file.", 'error');
        };
        reader.readAsBinaryString(file);
    };

    const handleDownloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet(fromFleet ? [['Plate Number or VIN']] : [page.upload.template]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, fromFleet ? "Vehicles_Template.xlsx" : page.upload.templateName);
    };

    const handleGeneratePDF = async () => {
        if (selectedCars.length === 0) return;
        setGenerating(true);

        try {
            const container = exportTemplateRef.current;
            container.style.display = 'block';

            // Give a small delay for DOM to stabilize
            await new Promise(r => setTimeout(r, 300));

            const doc = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = doc.internal.pageSize.getWidth();

            for (let i = 0; i < selectedCars.length; i++) {
                const pageElement = container.children[i];
                if (!pageElement) continue;

                const canvas = await html2canvas(pageElement, {
                    scale: 2, // Optimized scale for speed and browser limits
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });

                const imgData = canvas.toDataURL('image/jpeg', 0.92);
                const imgProps = doc.getImageProperties(imgData);
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

                if (i > 0) doc.addPage();
                doc.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
            }

            doc.save(`${page.pdfName}${new Date().getTime()}.pdf`);
            container.style.display = 'none';
        } catch (error) {
            logger.error("PDF Generation failed", error);
            showToast("PDF Generation failed", 'error');
        } finally {
            setGenerating(false);
        }
    };

    if (loading) return fleet ? <LoadingSpinner /> : <Layout><LoadingSpinner /></Layout>;

    const detailsCard = (
        <Card title={page.detailsTitle}>
            <FormGrid cols={2}>
                {page.fields.map((field) => (
                    <FormField key={field.key} field={field} formData={formData} setFormData={setFormData} />
                ))}
            </FormGrid>
        </Card>
    );

    const body = (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

                {/* Left Side: Selection & Form */}
                {fromFleet ? (
                    <div className="space-y-6">
                        {detailsCard}

                        <VehicleUploadCard
                            onDownloadTemplate={handleDownloadTemplate}
                            fileInputRef={fileInputRef}
                            onFileChange={handleFleetFileUpload}
                        />

                        <VehicleSearchCard
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            results={filteredCars}
                            onSelect={handleSelectCar}
                            showYear={page.showYear}
                        />
                    </div>
                ) : (
                    <div className="space-y-6">
                        <VehicleUploadCard
                            description={page.upload.description}
                            onDownloadTemplate={handleDownloadTemplate}
                            fileInputRef={fileInputRef}
                            onFileChange={handleNewCarsUpload}
                            accept={page.upload.accept}
                        />

                        {detailsCard}
                    </div>
                )}

                {/* Right Side: Selected & Action */}
                <div className="space-y-6">
                    <SelectedVehiclesCard
                        {...(fromFleet ? {} : page.upload.selectedCard(selectedCars))}
                        selectedCars={selectedCars}
                        onRemove={handleRemoveCar}
                        onClearAll={handleClearAll}
                        onGenerate={handleGeneratePDF}
                        generating={generating}
                        numbered={page.numbered}
                    />
                </div>
            </div>

            {/* Hidden Template for PDF Rendering */}
            <div
                ref={exportTemplateRef}
                id={page.containerId}
                className="pdf-render-container"
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: '0',
                    width: '210mm',
                    background: 'white',
                    display: 'none',
                    ...(page.containerColor && { color: page.containerColor })
                }}
            >
                {selectedCars.map((car, index) => (
                    <LetterSheet
                        key={index}
                        id={page.pageId ? `${page.pageId}${index}` : undefined}
                        letter={page.letter}
                        formData={formData}
                        car={car}
                    />
                ))}
            </div>
        </>
    );

    if (fleet) return body;
    return (
        <Layout
            title={page.title}
            subtitle="Government Transactions"
            showHome={true}
            backPath="/government"
        >
            {body}
        </Layout>
    );
}
